// =============================================================================
// Multi-Milestone Cart Progress Bar
// Milestones: Free Shipping → Free Gift
// Compatible with Minimog / Shopify AJAX cart updates.
// =============================================================================

(function () {
  'use strict';

  let minimogSubscriptionsBound = false;
  let refreshTimer = null;
  let drawerObserverBound = false;

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Calculate progress percentage (0–100) mapped across the highest threshold.
   * @param {number} cartTotal   – cart total in cents (active currency)
   * @param {number} maxThreshold – highest milestone value in cents (active currency)
   */
  function calcProgressPct(cartTotal, maxThreshold) {
    if (!maxThreshold) return 0;
    return Math.min((cartTotal / maxThreshold) * 100, 100);
  }

  /**
   * Format money using Shopify.formatMoney when available, otherwise a simple fallback.
   * @param {number} cents
   * @param {string} currency – ISO code, e.g. 'DKK'
   */
  function formatMoney(cents, currency) {
    // MinimogSettings.money_format is the reliable source; Shopify.money_format may be undefined
    const moneyFormat = (window.MinimogSettings && window.MinimogSettings.money_format)
      || (window.Shopify && window.Shopify.money_format)
      || (window.theme && window.theme.moneyFormat);

    if (window.Shopify && window.Shopify.formatMoney && moneyFormat) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    // Fallback: Intl formatter — used only when Shopify.formatMoney or moneyFormat is unavailable
    const c = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const decimals = c === 'JPY' || c === 'KRW' ? 0 : 2;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: c,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(cents / 100);
  }

  /**
   * Read the active Shopify Markets conversion rate.
   * The theme stores milestone settings in the shop default currency.
   * @returns {number}
   */
  function getCurrencyRate() {
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  /**
   * Convert a threshold to active market currency cents.
   *
   * Thresholds are set per-market in Translate & Adapt and are already
   * in the active market currency units (e.g. 599 NOK, 599 EUR).
   * We only multiply × 100 to convert to cents for comparison with
   * cart.total_price which Shopify always returns in cents.
   *
   * We do NOT multiply by currency rate — that would double-convert
   * because the merchant has already set the correct value per market.
   *
   * @param {number} amountInActiveCurrencyUnits
   * @returns {number} cents in the active market currency
   */
  function convertThreshold(amountInActiveCurrencyUnits) {
    return Math.round(amountInActiveCurrencyUnits * 100);
  }

  /**
   * Extract a cart total in cents from known cart payload shapes.
   * @param {object|number|null} payload
   * @returns {number|null}
   */
  function getCartTotal(payload) {
    if (typeof payload === 'number' && !Number.isNaN(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== 'object') {
      return null;
    }

    if (typeof payload.total_price === 'number') {
      return payload.total_price;
    }

    if (payload.detail && typeof payload.detail.total_price === 'number') {
      return payload.detail.total_price;
    }

    if (payload.cart && typeof payload.cart.total_price === 'number') {
      return payload.cart.total_price;
    }

    return null;
  }

  /**
   * Extract cart item count from known cart payload shapes.
   * @param {object|null} payload
   * @returns {number|null}
   */
  function getCartItemCount(payload) {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    if (typeof payload.item_count === 'number') {
      return payload.item_count;
    }

    if (payload.detail && typeof payload.detail.item_count === 'number') {
      return payload.detail.item_count;
    }

    if (payload.cart && typeof payload.cart.item_count === 'number') {
      return payload.cart.item_count;
    }

    return null;
  }

  /**
   * Build the contextual message shown above the bar.
   * @param {number} cartTotal
   * @param {number} ms1 – free-shipping threshold (cents, active currency)
   * @param {number} ms2 – free-gift threshold (cents, active currency)
   * @param {object} labels – { shippingReminder, giftReminder, allDone }
   * @param {string} currency
   */
  function buildMessage(cartTotal, ms1, ms2, labels, currency) {
    if (cartTotal >= ms2) {
      return labels.allDone;
    } else if (cartTotal >= ms1) {
      const remaining = formatMoney(ms2 - cartTotal, currency);
      return (labels.giftReminder || "FREE SHIPPING unlocked! You're ${amount} away from a FREE GIFT")
        .replace('${amount}', '<span>' + remaining + '</span>');
    } else {
      const remaining = formatMoney(ms1 - cartTotal, currency);
      return (labels.shippingReminder || "You're ${amount} away from FREE SHIPPING")
        .replace('${amount}', '<span>' + remaining + '</span>');
    }
  }

  // ---------------------------------------------------------------------------
  // DOM update
  // ---------------------------------------------------------------------------

  function updateMilestone(root, cartTotal, hasItems) {
    if (!hasItems) {
      root.style.display = 'none';
      return;
    }

    root.style.removeProperty('display');

    // data-ms*-threshold is in base currency units (e.g. 500 for 500 kr)
    // convertThreshold multiplies by rate × 100 to get active-currency cents
    const ms1 = convertThreshold(parseFloat(root.dataset.ms1Threshold) || 0);
    const ms2 = convertThreshold(parseFloat(root.dataset.ms2Threshold) || 0);
    if (!ms2) return;

    const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    const labels = {
      shippingReminder: root.dataset.shippingReminder,
      giftReminder: root.dataset.giftReminder,
      allDone: root.dataset.allDone,
    };

    const pct = calcProgressPct(cartTotal, ms2);
    const ms1Done = cartTotal >= ms1;
    const ms2Done = cartTotal >= ms2;

    // --- root modifier classes ---
    root.classList.toggle('m-cart-milestone--step1-done', ms1Done && !ms2Done);
    root.classList.toggle('m-cart-milestone--all-done', ms2Done);

    // --- fill ---
    const fill = root.querySelector('[data-milestone-fill]');
    if (fill) fill.style.width = pct + '%';

    // --- ARIA ---
    const barWrap = root.querySelector('.m-cart-milestone__bar-wrap');
    if (barWrap) barWrap.setAttribute('aria-valuenow', Math.round(pct));

    // --- markers ---
    root.querySelectorAll('[data-milestone-marker]').forEach(function (marker) {
      const idx = parseInt(marker.dataset.milestoneMarker, 10);
      const threshold = idx === 1 ? ms1 : ms2;
      const prevThreshold = idx === 1 ? 0 : ms1;
      const done = cartTotal >= threshold;
      const active = !done && cartTotal >= prevThreshold;

      marker.classList.toggle('m-cart-milestone__marker--completed', done);
      marker.classList.toggle('m-cart-milestone__marker--active', active);
    });

    // --- message ---
    const msgEl = root.querySelector('[data-milestone-msg]');
    if (msgEl) msgEl.innerHTML = buildMessage(cartTotal, ms1, ms2, labels, currency);
  }

  // ---------------------------------------------------------------------------
  // Main refresh — fetch cart and update all milestone bars on the page
  // ---------------------------------------------------------------------------

  function updateAllMilestones(cartTotal, hasItems) {
    const roots = document.querySelectorAll('[data-cart-milestone]');
    if (!roots.length) return;

    roots.forEach(function (root) {
      updateMilestone(root, cartTotal, hasItems);
    });
  }

  function refreshMilestones(payload) {
    const roots = document.querySelectorAll('[data-cart-milestone]');
    if (!roots.length) return;

    const cartTotal = getCartTotal(payload);
    const itemCount = getCartItemCount(payload);

    if (cartTotal !== null && itemCount !== null) {
      updateAllMilestones(cartTotal, itemCount > 0);
      return;
    }

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateAllMilestones(cart.total_price, (cart.item_count || 0) > 0);
      })
      .catch(function (err) {
        console.warn('[cart-progress-bar] Could not fetch cart:', err);
      });
  }

  function scheduleRefresh(payload) {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    refreshTimer = window.setTimeout(function () {
      refreshTimer = null;
      refreshMilestones(payload);
    }, 80);
  }

  function bindMinimogSubscriptions() {
    if (minimogSubscriptionsBound || !window.MinimogEvents || typeof window.MinimogEvents.subscribe !== 'function') {
      return false;
    }

    minimogSubscriptionsBound = true;
    window.MinimogEvents.subscribe('ON_ITEM_ADDED', scheduleRefresh);
    window.MinimogEvents.subscribe('ON_CART_UPDATE', scheduleRefresh);
    window.MinimogEvents.subscribe('ON_OPEN_DRAWER_CART', scheduleRefresh);

    return true;
  }

  function bindDrawerObserver() {
    if (drawerObserverBound || !window.MutationObserver || !document.body) {
      return;
    }

    drawerObserverBound = true;

    const observer = new MutationObserver(function (mutations) {
      const hasMilestoneNode = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          if (!node || node.nodeType !== 1) {
            return false;
          }

          if (node.matches && node.matches('[data-cart-milestone]')) {
            return true;
          }

          return Boolean(node.querySelector && node.querySelector('[data-cart-milestone]'));
        });
      });

      if (hasMilestoneNode) {
        scheduleRefresh();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function patchShopifyCartCallbacks() {
    if (!window.Shopify) {
      window.Shopify = {};
    }

    if (!window.Shopify.__cartMilestoneOnCartUpdatePatched) {
      const originalOnCartUpdate = window.Shopify.onCartUpdate;

      window.Shopify.onCartUpdate = function () {
        const args = arguments;
        const result = typeof originalOnCartUpdate === 'function'
          ? originalOnCartUpdate.apply(this, args)
          : undefined;

        Promise.resolve(result).finally(function () {
          scheduleRefresh(args[0]);
        });

        return result;
      };

      window.Shopify.__cartMilestoneOnCartUpdatePatched = true;
    }

    if (!window.Shopify.__cartMilestoneOnItemAddedPatched) {
      const originalOnItemAdded = window.Shopify.onItemAdded;

      window.Shopify.onItemAdded = function () {
        const args = arguments;
        const result = typeof originalOnItemAdded === 'function'
          ? originalOnItemAdded.apply(this, args)
          : undefined;

        Promise.resolve(result).finally(function () {
          scheduleRefresh();
        });

        return result;
      };

      window.Shopify.__cartMilestoneOnItemAddedPatched = true;
    }
  }

  function init() {
    scheduleRefresh();

    document.addEventListener('cart:updated', function (event) {
      scheduleRefresh(event);
    });
    document.addEventListener('cart:item-added', function (event) {
      scheduleRefresh(event);
    });

    bindMinimogSubscriptions();
    patchShopifyCartCallbacks();
    bindDrawerObserver();

    window.setTimeout(bindMinimogSubscriptions, 250);
    window.setTimeout(bindMinimogSubscriptions, 1000);
    window.setTimeout(scheduleRefresh, 400);
  }

  // ---------------------------------------------------------------------------
  // Initialisation & event wiring
  // ---------------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose helper for manual calls (e.g. from theme scripts)
  window.CartMilestone = {
    refresh: refreshMilestones,
    scheduleRefresh: scheduleRefresh,
  };

})();